import { supabase } from '@/integrations/supabase/client';
import { startOfDay, endOfDay, eachDayOfInterval, format } from 'date-fns';

export interface DailyNotificationStats {
  date: string;
  total: number;
  sent: number;
  failed: number;
  pending: number;
  successRate: number;
}

export interface NotificationTrends {
  daily: DailyNotificationStats[];
  summary: {
    totalNotifications: number;
    totalSent: number;
    totalFailed: number;
    totalPending: number;
    overallSuccessRate: number;
    averagePerDay: number;
  };
}

export const notificationAnalyticsService = {
  async getNotificationTrends(
    startDate: Date,
    endDate: Date,
    entityType?: 'team' | 'venue' | 'coach' | 'all',
    teamId?: string
  ): Promise<NotificationTrends> {
    try {
      // Query all notifications in date range
      let query = supabase
        .from('notification_delivery_log' as any)
        .select('created_at, status, related_entity_type')
        .eq('notification_type', 'season_rollover')
        .gte('created_at', startOfDay(startDate).toISOString())
        .lte('created_at', endOfDay(endDate).toISOString())
        .order('created_at', { ascending: true });

      if (entityType && entityType !== 'all') {
        query = query.eq('related_entity_type', entityType);
      }

      if (teamId) {
        query = query.eq('related_entity_id', teamId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const notifications = (data as any[]) || [];

      // Generate array of all dates in range
      const dateRange = eachDayOfInterval({ start: startDate, end: endDate });

      // Group notifications by date
      const dailyStats: DailyNotificationStats[] = dateRange.map(date => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const dayNotifications = notifications.filter(n => 
          format(new Date(n.created_at), 'yyyy-MM-dd') === dateStr
        );

        const sent = dayNotifications.filter(n => n.status === 'sent').length;
        const failed = dayNotifications.filter(n => n.status === 'failed').length;
        const pending = dayNotifications.filter(n => n.status === 'pending').length;
        const total = dayNotifications.length;

        return {
          date: dateStr,
          total,
          sent,
          failed,
          pending,
          successRate: total > 0 ? (sent / total) * 100 : 0
        };
      });

      // Calculate summary statistics
      const totalNotifications = notifications.length;
      const totalSent = notifications.filter(n => n.status === 'sent').length;
      const totalFailed = notifications.filter(n => n.status === 'failed').length;
      const totalPending = notifications.filter(n => n.status === 'pending').length;
      const overallSuccessRate = totalNotifications > 0 
        ? (totalSent / totalNotifications) * 100 
        : 0;
      const averagePerDay = totalNotifications / dateRange.length;

      return {
        daily: dailyStats,
        summary: {
          totalNotifications,
          totalSent,
          totalFailed,
          totalPending,
          overallSuccessRate,
          averagePerDay
        }
      };
    } catch (error) {
      console.error('Error fetching notification trends:', error);
      throw error;
    }
  },

  async getHourlyDistribution(
    startDate: Date,
    endDate: Date
  ): Promise<{ hour: number; count: number }[]> {
    try {
      const { data, error } = await supabase
        .from('notification_delivery_log' as any)
        .select('created_at')
        .eq('notification_type', 'season_rollover')
        .gte('created_at', startOfDay(startDate).toISOString())
        .lte('created_at', endOfDay(endDate).toISOString());

      if (error) throw error;

      const notifications = (data as any[]) || [];

      // Group by hour
      const hourlyCount = Array(24).fill(0).map((_, hour) => {
        const count = notifications.filter(n => 
          new Date(n.created_at).getHours() === hour
        ).length;
        return { hour, count };
      });

      return hourlyCount;
    } catch (error) {
      console.error('Error fetching hourly distribution:', error);
      return [];
    }
  },

  async getEntityTypeBreakdown(
    startDate: Date,
    endDate: Date
  ): Promise<{ entityType: string; count: number; successRate: number }[]> {
    try {
      const { data, error } = await supabase
        .from('notification_delivery_log' as any)
        .select('related_entity_type, status')
        .eq('notification_type', 'season_rollover')
        .gte('created_at', startOfDay(startDate).toISOString())
        .lte('created_at', endOfDay(endDate).toISOString());

      if (error) throw error;

      const notifications = (data as any[]) || [];

      // Group by entity type
      const entityTypes = ['team', 'venue', 'coach'];
      const breakdown = entityTypes.map(type => {
        const typeNotifications = notifications.filter(n => 
          n.related_entity_type === type
        );
        const sent = typeNotifications.filter(n => n.status === 'sent').length;
        const total = typeNotifications.length;

        return {
          entityType: type.charAt(0).toUpperCase() + type.slice(1),
          count: total,
          successRate: total > 0 ? (sent / total) * 100 : 0
        };
      });

      return breakdown.filter(b => b.count > 0);
    } catch (error) {
      console.error('Error fetching entity type breakdown:', error);
      return [];
    }
  }
};
